import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';

interface PrivacyPolicyDialogProps {
  open: boolean;
  onClose: () => void;
}

function PrivacyPolicyDialog({ open, onClose }: PrivacyPolicyDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Privacy Policy & Terms of Service</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ '& > *': { mb: 2 } }}>
          <Typography variant="h6">AI-Generated Content Disclaimer</Typography>
          <Typography variant="body2" paragraph>
            Our service uses artificial intelligence to automatically generate and post responses to customer reviews. 
            By using this service, you acknowledge and agree that:
          </Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li>AI-generated responses may occasionally contain inaccuracies, errors, or inappropriate content ("hallucinations")</li>
              <li>Responses are posted automatically without prior review or approval</li>
              <li>You are solely responsible for monitoring all automated responses and editing or deleting them if necessary</li>
              <li>We are not liable for any damages, losses, reputational harm, or consequences resulting from AI-generated content</li>
              <li>You can edit or delete responses through your Google Business Profile at any time</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3 }}>Data Collection & Usage</Typography>
          <Typography variant="body2" paragraph>
            We collect and process the following information:
          </Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li>Google Business Profile information and reviews</li>
              <li>Account and authentication data</li>
              <li>Usage statistics and service interactions</li>
              <li>Payment and subscription information</li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            Your data is used solely to provide and improve our service. We do not sell your data to third parties.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3 }}>Service Limitations</Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li>Service availability is not guaranteed and may be interrupted</li>
              <li>We reserve the right to modify or discontinue features at any time</li>
              <li>Subscription limits apply based on your chosen plan</li>
              <li>You are responsible for maintaining the security of your account</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3 }}>Liability</Typography>
          <Typography variant="body2" paragraph>
            This service is provided "as is" without warranties of any kind. We are not responsible for:
          </Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li>Accuracy or appropriateness of AI-generated responses</li>
              <li>Damage to your business reputation from automated responses</li>
              <li>Loss of data or service interruptions</li>
              <li>Third-party service failures (Google, OpenAI, Stripe)</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3 }}>Cancellation & Refunds</Typography>
          <Typography variant="body2" paragraph>
            You may cancel your subscription at any time. Refunds are not provided for partial billing periods. 
            Your trial period can only be used once per account.
          </Typography>

          <Typography variant="body2" sx={{ mt: 3, fontStyle: 'italic' }}>
            Last updated: January 2026
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PrivacyPolicyDialog;
